import React, { useState } from "react";
import loadable from "@loadable/component";
import analytics from "web/script/analytics/analytics";
import browser from "web/script/utils/browser";
import { BREAKPOINTS } from "web/react/constants";
import Carousel from "web/react/components/product-gallery/product-gallery-carousel/product-gallery-carousel";
import { useLeadHref } from "web/react/components/lead-link/lead-link";

const FullscreenGallery = loadable(() =>
    import(
        /* webpackChunkName: "react-fullscreen-gallery" */ "web/react/components/product-gallery/product-gallery-fullscreen/product-gallery-fullscreen"
    )
);

interface ProductGalleryProps {
    images: ProductImage[];
    dotsInsideGallery?: boolean;
    relatedProducts?: ProductCard[];
    currentLinkId?: string;
    swipeRedirect?: boolean;
    clickLeadUrl: string;
    clickRedirect: boolean;
    hasRelatedProductsSidebar: boolean;
}

function ProductGallery({
    images,
    dotsInsideGallery,
    relatedProducts,
    currentLinkId,
    swipeRedirect,
    clickLeadUrl,
    clickRedirect,
    hasRelatedProductsSidebar,
}: ProductGalleryProps): React.ReactElement {
    const viewport = browser.getViewport() === browser.VIEWPORTS.DESKTOP ? "desktop" : "mobile";
    const analyticsAction = `${viewport}_gallery`;
    const [firstInteraction, setFirstInteraction] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const leadHref = useLeadHref(clickLeadUrl, "affiliate-is-pdp-gallery-image");

    function handleFirstInteraction(): void {
        setFirstInteraction(true);
        analytics.event("engagement", analyticsAction, "interacted");
    }

    function openFullscreenGallery(index): void {
        setCurrentSlideIndex(index);
        setFullscreen(true);
    }

    function handleImageClick(index): void {
        if (!firstInteraction) {
            handleFirstInteraction();
        }

        if (clickRedirect && clickLeadUrl) {
            analytics.event("buy_area", "clicked", "pdp-gallery-image");
            window.open(leadHref, "_blank");
        } else {
            openFullscreenGallery(index);
        }
    }

    // Slick settings and props for the gallery
    const gallerySettings = {
        speed: 0,
        initialSlide: 0,
        arrows: false,
        dots: images.length > 1,
        // The progressive option loads the first image on page load and the other images are loaded afterwards
        lazyLoad: "progressive",
        centerMode: false,
        slidesToShow: 1,
        infinite: false,
        variableWidth: false,
        swipe: false,
        touchMove: false,
        draggable: false,
        responsive: [
            {
                breakpoint: BREAKPOINTS.Medium,
                settings: {
                    speed: 200,
                    slidesToShow: 1.3,
                    infinite: true,
                    variableWidth: true,
                    swipe: true,
                    touchMove: true,
                    touchThreshold: 20,
                    draggable: true,
                },
            },
        ],
    };

    const galleryProps = {
        analyticsAction: analyticsAction,
        dotsInsideGallery: dotsInsideGallery,
        firstInteraction: firstInteraction,
        handleFirstInteraction: handleFirstInteraction,
        images: images,
        relatedProducts: relatedProducts,
        handleImageClick: (index): void => handleImageClick(index),
        openFullscreenGallery: (index): void => openFullscreenGallery(index),
        settings: gallerySettings,
        currentLinkId: currentLinkId,
        swipeRedirect: swipeRedirect,
        hasRelatedProductsSidebar: hasRelatedProductsSidebar,
    };

    // Slick settings and props for the fullscreen gallery
    const fullscreenGallerySettings = {
        speed: 400,
        initialSlide: currentSlideIndex,
        slidesToShow: 1,
        dots: images.length > 1,
        // The progressive option loads the first image on page load and the other images are loaded afterwards
        lazyLoad: false,
        centerMode: false,
        touchThreshold: 20,
        infinite: true,
        swipeToSlide: false,
        arrows: true,
        swipe: false,
        touchMove: false,
        draggable: false,
        responsive: [
            {
                breakpoint: BREAKPOINTS.Medium,
                settings: {
                    speed: 200,
                    arrows: false,
                    swipeToSlide: true,
                    swipe: true,
                    touchMove: true,
                    draggable: true,
                    lazyLoad: "progressive",
                },
            },
        ],
    };

    const fullscreenGalleryProps = {
        analyticsAction: analyticsAction,
        isOpen: fullscreen,
        onClose: (): void => setFullscreen(false),
        images: images,
        relatedProducts: relatedProducts,
        firstInteraction: firstInteraction,
        currentLinkId: currentLinkId,
        settings: fullscreenGallerySettings,
    };

    return (
        <>
            <Carousel {...galleryProps} />
            {fullscreen ? <FullscreenGallery {...fullscreenGalleryProps} /> : null}
        </>
    );
}

export default ProductGallery;

ProductGallery.displayName = "ProductGallery";
